import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, BookOpen, Newspaper, MessageSquare, TrendingUp, Clock, Sparkles,
  ShieldCheck, Landmark, Bot, CandlestickChart, LineChart, BadgeCheck, Lock, Eye, Radio, GraduationCap, BarChart3
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AiMarketSummary from '../components/AiMarketSummary';
import LiveMarketTicker from '../components/LiveMarketTicker';
import WeeklyBriefPreview from '../components/WeeklyBriefPreview';
import FearGreedGauge from '../components/FearGreedGauge';
import HeroBackground from '../components/HeroBackground';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContinueLearning = () => {
  const { isAuthenticated, getToken } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      axios.get(`${API}/learning-path/continue`, { headers: { Authorization: `Bearer ${getToken()}` } })
        .then(r => setData(r.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  if (!isAuthenticated || !data || !data.next_step) return null;

  return (
    <section className="relative -mt-2 pb-2">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="bg-gradient-to-r from-[#1a1508] to-[#111111] border border-[#C5A059]/40 rounded-xl px-6 py-5 flex flex-wrap items-center justify-between gap-4" data-testid="continue-learning-module">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-11 h-11 rounded-xl bg-[#C5A059]/15 flex items-center justify-center flex-shrink-0">
              <GraduationCap size={22} className="text-[#C5A059]" />
            </div>
            <div className="min-w-0">
              <p className="text-[#C5A059] text-[11px] font-bold uppercase tracking-wider">
                {data.started ? 'Continue where you left off' : 'Start your learning journey'} · {data.pct}% complete
              </p>
              <p className="text-white font-semibold text-sm truncate" data-testid="continue-next-step">
                {data.next_step.tier}: {data.next_step.title}
              </p>
              <div className="h-1 bg-white/10 rounded-full overflow-hidden mt-1.5 max-w-xs">
                <div className="h-full bg-gradient-to-r from-[#C5A059] to-[#D4AF37] rounded-full" style={{ width: `${data.pct}%` }} />
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Link to={data.next_step.path}>
              <Button className="bg-[#C5A059] text-black hover:bg-[#D4AF37] rounded-sm font-semibold text-xs" data-testid="continue-resume-btn">
                {data.started ? 'Resume' : 'Start'} <ArrowRight size={14} className="ml-1" />
              </Button>
            </Link>
            <Link to="/start-here">
              <Button variant="outline" className="border-white/20 text-gray-300 hover:bg-white/5 rounded-sm text-xs" data-testid="continue-view-path-btn">
                View Path
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const LandingPage = () => {
  const { login } = useAuth();
  const [latestUpdates, setLatestUpdates] = useState([]);
  const [liveNews, setLiveNews] = useState([]);
  const [trackStats, setTrackStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 5 * 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    try {
      const [articlesRes, newsRes, statsRes, liveRes] = await Promise.allSettled([
        axios.get(`${API}/education/articles`),
        axios.get(`${API}/news/posts`),
        axios.get(`${API}/track-record/stats`),
        axios.get(`${API}/live-news/latest?limit=4`),
      ]);

      if (articlesRes.status === 'fulfilled' && newsRes.status === 'fulfilled') {
        const combined = [
          ...articlesRes.value.data.slice(0, 3).map(item => ({ ...item, type: 'education' })),
          ...newsRes.value.data.slice(0, 3).map(item => ({ ...item, type: 'news' }))
        ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 6);
        setLatestUpdates(combined);
      }
      if (statsRes.status === 'fulfilled') setTrackStats(statsRes.value.data);
      if (liveRes.status === 'fulfilled') setLiveNews(liveRes.value.data.articles || []);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const getUpdateLink = (u) => u.type === 'education' ? `/education/${u.article_id}` : `/news/${u.news_id}`;

  const timeAgo = (iso) => {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const tools = [
    {
      icon: Landmark, name: 'Institutional Analysis', to: '/analysis', badge: 'PRO',
      desc: 'Wyckoff, order blocks, volume profile & Elliott Wave combined into a probability-weighted forecast.'
    },
    {
      icon: Bot, name: 'AI Research Agent', to: '/ai-advisor', badge: 'PRO',
      desc: 'Chat with an AI analyst wired to real-time market data. Upload charts for instant technical reads.'
    },
    {
      icon: CandlestickChart, name: 'Demo Trading Arena', to: '/trading', badge: 'FREE',
      desc: 'Trade $100k virtual capital on live crypto prices. Climb the leaderboard risk-free.'
    },
    {
      icon: LineChart, name: 'Pattern Library', to: '/patterns', badge: 'FREE',
      desc: '43 professional chart patterns with real examples, reliability scores and quizzes.'
    },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <LiveMarketTicker />
      <Navbar />

      {/* ===== HERO ===== */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <HeroBackground />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-full px-4 py-1.5 mb-6" data-testid="hero-trust-badge">
            <BadgeCheck className="text-[#C5A059]" size={15} />
            <span className="text-[#C5A059] text-xs font-semibold uppercase tracking-wider">Publicly Verified AI Track Record</span>
          </div>

          <h1 className="font-manrope font-bold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl mb-6" data-testid="hero-headline">
            Preparing For The
            <br />
            <span className="text-[#C5A059]">New Financial Order</span>
          </h1>
          <p className="font-inter text-base sm:text-lg text-gray-300 max-w-3xl mx-auto mb-8 leading-relaxed">
            Institutional-grade market intelligence, AI analysis and financial education —
            with every AI call tracked publicly, win or lose.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-10">
            <Button
              onClick={login}
              className="bg-[#C5A059] text-black hover:bg-[#D4AF37] rounded-sm px-8 py-3 font-semibold uppercase text-sm shadow-[0_0_15px_rgba(197,160,89,0.3)] hover:shadow-[0_0_25px_rgba(197,160,89,0.5)] transition-all"
              data-testid="hero-join-community"
            >
              Join Community
            </Button>
            <Link to="/start-here">
              <Button
                variant="outline"
                className="border border-white/20 bg-transparent text-white hover:bg-white/5 rounded-sm px-8 py-3 font-semibold uppercase text-sm transition-all"
                data-testid="hero-start-learning"
              >
                Start Learning
              </Button>
            </Link>
            <Link to="/news">
              <Button
                variant="ghost"
                className="text-gray-300 hover:text-[#C5A059] transition-colors"
                data-testid="hero-explore-news"
              >
                Explore Market News <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>

          {/* Trust indicators row */}
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-3 text-xs text-gray-500" data-testid="hero-trust-row">
            <span className="inline-flex items-center gap-1.5"><Eye size={13} className="text-[#C5A059]" /> Transparent — every call published</span>
            <span className="inline-flex items-center gap-1.5"><Radio size={13} className="text-emerald-500" /> Live market data 24/7</span>
            <span className="inline-flex items-center gap-1.5"><Lock size={13} className="text-[#007AFF]" /> Payments secured by Stripe</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={13} className="text-[#C5A059]" /> Education-first, no hype</span>
          </div>
        </div>
      </section>

      {/* ===== CREDIBILITY STATS STRIP ===== */}
      <section className="relative border-y border-white/10 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-testid="credibility-strip">
            <div className="text-center">
              <p className="font-manrope font-bold text-3xl sm:text-4xl text-[#C5A059]" data-testid="stat-calls-tracked">
                {trackStats?.overall?.total ?? '—'}
              </p>
              <p className="text-gray-500 text-xs uppercase tracking-wider mt-1">AI Calls Tracked (90d)</p>
            </div>
            <div className="text-center">
              {(() => {
                const eng = trackStats?.engines?.consensus;
                const useV2 = eng && eng.total >= 10 && eng.accuracy_24h != null;
                const acc = useV2 ? eng.accuracy_24h : trackStats?.overall?.accuracy_24h;
                return (
                  <>
                    <p className="font-manrope font-bold text-3xl sm:text-4xl text-white" data-testid="stat-accuracy">
                      {acc != null ? `${acc}%` : '—'}
                    </p>
                    <p className="text-gray-500 text-xs uppercase tracking-wider mt-1">
                      Verified 24h Accuracy{useV2 && <span className="text-[#C5A059]"> · Engine v2</span>}
                    </p>
                  </>
                );
              })()}
            </div>
            <div className="text-center">
              <p className="font-manrope font-bold text-3xl sm:text-4xl text-white">43</p>
              <p className="text-gray-500 text-xs uppercase tracking-wider mt-1">Chart Patterns Taught</p>
            </div>
            <div className="text-center">
              <p className="font-manrope font-bold text-3xl sm:text-4xl text-[#007AFF]">24/7</p>
              <p className="text-gray-500 text-xs uppercase tracking-wider mt-1">Live Market Coverage</p>
            </div>
          </div>
          <div className="text-center mt-6">
            <Link to="/track-record" className="text-[#C5A059] text-sm font-medium hover:text-[#D4AF37] transition-colors inline-flex items-center gap-1" data-testid="verify-track-record-link">
              <BadgeCheck size={15} /> Don't trust us — verify every call yourself <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== CONTINUE LEARNING (returning users) ===== */}
      <ContinueLearning />

      {/* ===== AI MARKET SUMMARY + LIVE HEADLINES ===== */}
      <section className="py-16 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <AiMarketSummary />
            </div>

            {/* Live headlines panel */}
            <div className="bg-[#111111] border border-white/10 rounded-lg p-5" data-testid="home-live-news">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-white inline-flex items-center gap-2">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </span>
                  Live Headlines
                </h4>
                <Link to="/news" className="text-xs text-[#007AFF] hover:text-[#2E90FA] transition-colors">View all</Link>
              </div>
              <div className="space-y-3">
                {liveNews.length === 0 ? (
                  <p className="text-gray-600 text-sm">Loading headlines...</p>
                ) : liveNews.map((a, i) => (
                  <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer"
                    className="block border-b border-white/5 pb-3 last:border-0 last:pb-0 group"
                    data-testid={`home-headline-${i}`}>
                    <p className="text-sm text-gray-300 group-hover:text-[#007AFF] transition-colors leading-snug line-clamp-2">{a.title}</p>
                    <p className="text-[11px] text-gray-600 mt-1">{a.source} · {timeAgo(a.published_at)}</p>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PROFESSIONAL TOOLKIT ===== */}
      <section className="py-16 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-left mb-10 max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-full px-4 py-1.5 mb-4">
              <Landmark className="text-[#C5A059]" size={15} />
              <span className="text-[#C5A059] font-semibold text-xs uppercase tracking-wider">Professional Toolkit</span>
            </div>
            <h2 className="font-manrope font-bold text-3xl sm:text-4xl text-white mb-3">
              Tools built like a <span className="text-[#C5A059]">trading desk</span>
            </h2>
            <p className="text-gray-400 text-base">
              From institutional technical analysis to a risk-free trading arena — everything you need to learn, analyze and execute.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="toolkit-grid">
            {tools.map((tool) => {
              const Icon = tool.icon;
              return (
                <Link key={tool.to} to={tool.to}
                  className="bg-[#111111] border border-white/10 rounded-lg p-6 hover:border-[#C5A059]/50 hover:shadow-[0_0_25px_rgba(197,160,89,0.08)] transition-all duration-300 group hover-lift flex flex-col"
                  data-testid={`tool-card-${tool.to.replace('/', '')}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-[#C5A059]/15 rounded-sm flex items-center justify-center">
                      <Icon className="text-[#C5A059]" size={24} />
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${tool.badge === 'PRO' ? 'bg-[#C5A059]/20 text-[#C5A059] border border-[#C5A059]/40' : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'}`}>
                      {tool.badge}
                    </span>
                  </div>
                  <h3 className="font-manrope font-semibold text-white text-lg mb-2 group-hover:text-[#C5A059] transition-colors">{tool.name}</h3>
                  <p className="text-gray-400 text-sm mb-4 flex-1">{tool.desc}</p>
                  <span className="text-[#C5A059] text-sm font-medium inline-flex items-center">
                    Open <ArrowRight className="ml-1 group-hover:translate-x-1 transition-transform" size={14} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== WHY TRUST US ===== */}
      <section className="py-16 bg-[#0D0D0D] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-full px-4 py-1.5 mb-4">
                <ShieldCheck className="text-[#C5A059]" size={15} />
                <span className="text-[#C5A059] font-semibold text-xs uppercase tracking-wider">Radical Transparency</span>
              </div>
              <h2 className="font-manrope font-bold text-3xl sm:text-4xl text-white mb-4">
                Why people <span className="text-[#C5A059]">trust</span> DigitalGoldGang
              </h2>
              <p className="text-gray-400 text-base leading-relaxed mb-6">
                Most market channels only show their winners. We publish <span className="text-white font-medium">every single AI market call</span> —
                bullish or bearish, right or wrong — and verify each one against real price data 24h, 48h and 7 days later.
                No cherry-picking, no deleted calls.
              </p>
              <Link to="/track-record">
                <Button className="bg-transparent border border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-black rounded-sm px-6 py-3 font-semibold transition-all" data-testid="trust-cta-track-record">
                  Inspect The Full Track Record <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="trust-pillars">
              {[
                { icon: Eye, title: 'Public Track Record', desc: 'Every AI call logged with timestamp and outcome — verifiable by anyone.' },
                { icon: Radio, title: 'Real Data Only', desc: 'Live prices from institutional-grade sources. No delayed or simulated feeds.' },
                { icon: Lock, title: 'Secure & Compliant', desc: 'Stripe-secured payments, Google sign-in, clear disclaimers on every page.' },
                { icon: BookOpen, title: 'Education First', desc: 'We teach the "why" behind every signal. No pump groups, no financial advice.' },
              ].map((p, i) => {
                const Icon = p.icon;
                return (
                  <div key={i} className="bg-[#111111] border border-white/10 rounded-lg p-5">
                    <Icon className="text-[#C5A059] mb-3" size={20} />
                    <h4 className="font-semibold text-white text-sm mb-1">{p.title}</h4>
                    <p className="text-gray-500 text-xs leading-relaxed">{p.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ===== FEAR & GREED INDEX ===== */}
      <section className="py-16 relative" data-testid="fear-greed-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-full px-4 py-2 mb-4">
              <BarChart3 className="text-[#C5A059]" size={16} />
              <span className="text-[#C5A059] font-semibold text-sm uppercase tracking-wider">Institutional Sentiment</span>
            </div>
            <h2 className="font-manrope font-bold text-3xl sm:text-4xl text-white mb-2">
              Fear &amp; Greed <span className="text-[#C5A059]">Index</span>
            </h2>
            <p className="font-manrope font-extrabold text-lg tracking-[0.2em] uppercase">
              <span className="text-red-400">Buy the Fear</span>
              <span className="text-gray-600 mx-2">·</span>
              <span className="text-emerald-400">Sell the Greed</span>
            </p>
          </div>
          <div className="max-w-5xl mx-auto">
            <FearGreedGauge />
          </div>
        </div>
      </section>

      {/* ===== WEEKLY MACRO BRIEF ===== */}
      <section className="py-16 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-full px-4 py-2 mb-4">
              <TrendingUp className="text-[#C5A059]" size={16} />
              <span className="text-[#C5A059] font-semibold text-sm uppercase tracking-wider">Institutional Research</span>
            </div>
            <h2 className="font-manrope font-bold text-3xl sm:text-4xl text-white mb-4">
              Weekly Institutional <span className="text-[#C5A059]">Macro Brief</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Institutional-grade macro analysis and market intelligence for serious investors
            </p>
          </div>
          <div className="max-w-3xl mx-auto">
            <WeeklyBriefPreview />
          </div>
        </div>
      </section>

      {/* ===== LATEST INTELLIGENCE ===== */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#111111] to-[#0A0A0A]"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-full px-4 py-2 mb-4">
              <Sparkles className="text-[#C5A059]" size={20} />
              <span className="text-[#C5A059] font-semibold text-sm uppercase">Live Updates</span>
            </div>
            <h2 className="font-manrope font-bold text-3xl sm:text-4xl text-white mb-4">Latest Intelligence</h2>
            <p className="text-gray-400 text-lg">Real-time updates from education, news, and market analysis</p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#C5A059]"></div>
            </div>
          ) : latestUpdates.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400">No updates yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestUpdates.map((update, index) => (
                <Link
                  key={`${update.type}-${update.article_id || update.news_id}`}
                  to={getUpdateLink(update)}
                  className="bg-[#111111] border border-white/10 rounded-lg p-6 hover:border-[#C5A059]/50 hover:shadow-lg transition-all duration-300 group hover-lift"
                  data-testid={`latest-update-${index}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {update.type === 'education' ? (
                        <>
                          <BookOpen className="text-[#C5A059]" size={16} />
                          <span className="text-[#C5A059] text-xs font-semibold uppercase">Education</span>
                        </>
                      ) : (
                        <>
                          <Newspaper className="text-[#007AFF]" size={16} />
                          <span className="text-[#007AFF] text-xs font-semibold uppercase">News</span>
                        </>
                      )}
                    </div>
                    {update.featured && (
                      <span className="bg-[#C5A059]/20 text-[#C5A059] text-xs px-2 py-1 rounded-full">Featured</span>
                    )}
                  </div>
                  <h3 className="font-manrope font-semibold text-white text-lg mb-2 group-hover:text-[#C5A059] transition-colors line-clamp-2">
                    {update.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {update.summary || update.content?.substring(0, 150) + '...'}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                      <Clock size={12} />
                      <span>{new Date(update.created_at).toLocaleDateString()}</span>
                    </div>
                    <span className="text-[#C5A059] group-hover:text-[#D4AF37] transition-colors font-medium">Read More →</span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/education" className="inline-block">
              <Button variant="outline" className="border border-[#C5A059] bg-transparent text-[#C5A059] hover:bg-[#C5A059] hover:text-black rounded-sm px-8 py-3 font-semibold transition-all mr-4">
                View All Articles
              </Button>
            </Link>
            <Link to="/news" className="inline-block">
              <Button variant="outline" className="border border-[#007AFF] bg-transparent text-[#007AFF] hover:bg-[#007AFF] hover:text-white rounded-sm px-8 py-3 font-semibold transition-all">
                View All News
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== SECURITY & TRUST ===== */}
      <section className="py-16 bg-[#0D0D0D] border-y border-white/10" data-testid="security-trust-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-10">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-full px-4 py-2 mb-4">
                <ShieldCheck className="text-[#C5A059]" size={16} />
                <span className="text-[#C5A059] font-semibold text-sm uppercase tracking-wider">Security & Trust</span>
              </div>
              <h2 className="font-manrope font-bold text-3xl sm:text-4xl text-white">
                Built Like a <span className="text-[#C5A059]">Vault</span>
              </h2>
            </div>
            <p className="text-gray-400 text-base max-w-md">
              Your data, conversations, and payments are protected by industry-standard security at every layer.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Lock, title: 'Private AI Conversations', desc: 'Your AI Advisor chats are encrypted in transit and visible only to you — no one else can read or delete them.' },
              { icon: BadgeCheck, title: 'Google-Verified Sign-In', desc: 'Authentication handled by Google OAuth. We never see or store your password.' },
              { icon: ShieldCheck, title: 'Stripe-Secured Payments', desc: 'Pro subscriptions run through Stripe, a PCI-DSS Level 1 certified processor. Card details never touch our servers.' },
              { icon: Eye, title: 'Privacy by Default', desc: 'Public profiles never expose your email. Sessions auto-expire and all content is sanitized against injection attacks.' },
            ].map((item, i) => (
              <div
                key={item.title}
                className="bg-[#111111] border border-white/10 rounded-lg p-6 hover:border-[#C5A059]/40 transition-all duration-300 hover-lift"
                data-testid={`trust-card-${i}`}
              >
                <div className="w-11 h-11 rounded-sm bg-[#C5A059]/10 border border-[#C5A059]/30 flex items-center justify-center mb-4">
                  <item.icon className="text-[#C5A059]" size={20} />
                </div>
                <h3 className="font-manrope font-semibold text-white text-base mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== COMMUNITY + MISSION ===== */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <MessageSquare className="mx-auto text-[#C5A059] mb-5" size={28} />
          <h2 className="font-manrope font-bold text-3xl sm:text-4xl text-white mb-6">Our Mission</h2>
          <p className="font-manrope font-semibold text-xl text-[#C5A059] mb-4">
            Educate. Analyze. Prepare. Execute.
          </p>
          <p className="text-gray-300 text-base leading-relaxed mb-8">
            We bridge macro economics, blockchain transparency, and community-driven analysis
            to help individuals prepare for the next era of finance. Join a community of investors
            building their financial intelligence.
          </p>
          <Button
            onClick={login}
            className="bg-[#C5A059] text-black hover:bg-[#D4AF37] rounded-sm px-8 py-3 font-semibold uppercase text-sm shadow-[0_0_15px_rgba(197,160,89,0.3)] transition-all"
            data-testid="mission-join-cta"
          >
            Join The Gang — Free
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
